import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Calendar, dateFnsLocalizer, ToolbarProps, View } from 'react-big-calendar';
import { DownloadOutlined, MailOutlined } from '@ant-design/icons';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { apiFetch } from '../../utils/apiFetch';

const locales = { 'en-GB': require('date-fns/locale/en-GB') };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

/* ── Custom calendar toolbar: all controls in one compact row ── */
const CalendarToolbar = ({ label, onNavigate, onView, view }: ToolbarProps) => {
  const views: View[] = ['month', 'week', 'day', 'agenda'];
  const viewLabels: Record<string, string> = { month: 'Month', week: 'Week', day: 'Day', agenda: 'Agenda' };
  const btnBase: React.CSSProperties = {
    padding: '5px 12px', borderRadius: 6, border: '1px solid #e5e7eb',
    background: '#fff', color: '#374151', fontWeight: 600, fontSize: 12,
    cursor: 'pointer', fontFamily: 'inherit',
  };
  const activeView: React.CSSProperties = { background: '#3b82f6', color: '#fff', borderColor: '#3b82f6' };
  return (
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
      {/* Nav */}
      <button style={btnBase} onClick={() => onNavigate('TODAY')}>Today</button>
      <button style={btnBase} onClick={() => onNavigate('PREV')}>‹ Back</button>
      <button style={btnBase} onClick={() => onNavigate('NEXT')}>Next ›</button>
      {/* Date label */}
      <span style={{ flex: 1, textAlign: 'center', fontWeight: 700, fontSize: 14, color: '#111827', padding: '0 8px' }}>{label}</span>
      {/* View buttons */}
      {views.map(v => (
        <button key={v} style={{ ...btnBase, ...(view === v ? activeView : {}) }} onClick={() => onView(v)}>
          {viewLabels[v]}
        </button>
      ))}
    </div>
  );
};

// ─── Consistent status palette used everywhere ────────────────────────────────
const STATUS: Record<string, { bg: string; text: string; dot: string }> = {
  Pending:   { bg: '#fff7e6', text: '#b45309', dot: '#f59e0b' },
  Confirmed: { bg: '#f0fdf4', text: '#166534', dot: '#22c55e' },
  Completed: { bg: '#eff6ff', text: '#1e40af', dot: '#3b82f6' },
  Cancelled: { bg: '#fef2f2', text: '#991b1b', dot: '#ef4444' },
};
const getStatus = (s: string) => STATUS[s] || STATUS.Pending;

const STAFF_COLORS = [
  '#3b82f6','#22c55e','#8b5cf6','#f97316','#ec4899',
  '#14b8a6','#ef4444','#84cc16','#f59e0b','#6366f1'
];

type Customer = { id: string; name: string; email?: string };
interface Staff { id: string; name: string; role?: string }

type BookingRow = {
  id: string;
  service: string;
  date?: string;
  time?: string;
  start?: string;
  end?: string;
  start_time?: string;
  end_time?: string;
  startTime?: string;
  endTime?: string;
  duration?: number;
  status: string;
  notes?: string;
  customer_name?: string;
  customerName?: string;
  customer_email?: string;
  customerEmail?: string;
  customer_phone?: string;
  customerPhone?: string;
  staff_name?: string;
  staffName?: string;
  staff_id?: string;
  customer_id?: string;
  customerId?: string;
  location?: string;
  meetingPlatform?: string;
  meetingLink?: string;
  price?: number;
  created_at?: string;
  updated_by?: string;
  updated_at?: string;
};

const TIME_SLOTS = [
  '08:00','08:30','09:00','09:30',
  '10:00','10:30','11:00','11:30',
  '12:00','12:30','13:00','13:30',
  '14:00','14:30','15:00','15:30',
  '16:00','16:30','17:00','17:30',
  '18:00','18:30','19:00','19:30',
];

// ─── Helper: format a booking's date+time for display ────────────────────────
const fmtDateTime = (b: BookingRow) => {
  const raw = b.start_time || b.startTime || b.start || '';
  if (raw) {
    try {
      const d = new Date(raw);
      if (!isNaN(d.getTime()))
        return d.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
    } catch { /* fallthrough */ }
  }
  if (b.date && b.time) return `${b.date} ${b.time}`;
  if (b.date) return b.date;
  return '-';
};

// ─── Full detail drawer shown when user clicks a booking ─────────────────────
const BookingDrawer: React.FC<{
  booking: BookingRow;
  onClose: () => void;
  onSave: (id: string, patch: Partial<BookingRow>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}> = ({ booking, onClose, onSave, onDelete }) => {
  const [service, setService]   = useState(booking.service || '');
  const [status, setStatus]     = useState(booking.status  || 'Pending');
  const [notes, setNotes]       = useState(booking.notes   || '');
  const [location, setLocation] = useState(booking.location || '');
  const [meetingLink, setMeetingLink] = useState(booking.meetingLink || '');
  const [saving, setSaving]     = useState(false);

  const sc = getStatus(status);
  const custName  = booking.customer_name  || booking.customerName  || 'Guest';
  const custEmail = booking.customer_email || booking.customerEmail || '';
  const custPhone = booking.customer_phone || booking.customerPhone || '';
  const staffName = booking.staff_name     || booking.staffName     || '';

  const handleSave = async () => {
    setSaving(true);
    await onSave(booking.id, { service, status, notes, location, meetingLink });
    setSaving(false);
  };

  return (
    // Backdrop
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.38)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
    >
      {/* Panel */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 14, width: '100%', maxWidth: 520,
          maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.20)',
          fontFamily: "'DM Sans', system-ui, sans-serif",
        }}
      >
        {/* Header strip — coloured by status */}
        <div style={{
          background: sc.dot, borderRadius: '14px 14px 0 0',
          padding: '16px 20px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Booking #{booking.id}
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', marginTop: 2 }}>
              {booking.service}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'rgba(255,255,255,0.22)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 18, width: 34, height: 34, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: '20px 20px 24px' }}>

          {/* Info grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', marginBottom: 20 }}>
            <InfoRow icon="🕐" label="Date & Time" value={fmtDateTime(booking)} />
            <InfoRow icon="⏱" label="Duration" value={booking.duration ? `${booking.duration} min` : '60 min'} />
            <InfoRow icon="👤" label="Customer" value={custName} />
            {custEmail && <InfoRow icon="📧" label="Email" value={custEmail} />}
            {custPhone && <InfoRow icon="📱" label="Phone" value={custPhone} />}
            {staffName && <InfoRow icon="🧑‍💼" label="Staff" value={staffName} />}
            {booking.price != null && <InfoRow icon="💷" label="Price" value={`£${Number(booking.price).toFixed(2)}`} />}
            {booking.meetingPlatform && <InfoRow icon="🖥" label="Platform" value={booking.meetingPlatform} />}
          </div>

          {/* Updated by */}
          {booking.updated_by && (
            <div style={{ marginBottom: 16, padding: '8px 12px', background: '#f0f7ff', borderRadius: 8, fontSize: 12, color: '#555' }}>
              Last updated by <strong>{booking.updated_by}</strong>
              {booking.updated_at ? ` · ${new Date(booking.updated_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}` : ''}
            </div>
          )}

          <div style={{ height: 1, background: '#f3f4f6', marginBottom: 16 }} />

          {/* Editable fields */}
          <Field label="Service">
            <input value={service} onChange={e => setService(e.target.value)} style={inp} />
          </Field>

          <Field label="Status">
            <select value={status} onChange={e => setStatus(e.target.value)} style={inp}>
              <option value="Pending">⏳ Pending</option>
              <option value="Confirmed">✅ Confirmed</option>
              <option value="Completed">🎉 Completed</option>
              <option value="Cancelled">❌ Cancelled</option>
            </select>
          </Field>

          <Field label="Location">
            <input value={location} onChange={e => setLocation(e.target.value)} style={inp} placeholder="Office / address / remote" />
          </Field>

          <Field label="Meeting Link">
            <input value={meetingLink} onChange={e => setMeetingLink(e.target.value)} style={inp} placeholder="https://..." />
          </Field>

          <Field label="Notes">
            <textarea value={notes} onChange={e => setNotes(e.target.value)} style={{ ...inp, height: 72, resize: 'vertical' }} placeholder="Notes..." />
          </Field>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 8 }}>
            <button
              onClick={() => onDelete(booking.id)}
              style={{ padding: '9px 16px', borderRadius: 8, border: '1.5px solid #fca5a5', background: '#fff', color: '#dc2626', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
            >
              🗑 Delete
            </button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={onClose} style={{ padding: '9px 16px', borderRadius: 8, border: '1.5px solid #e5e7eb', background: '#fff', color: '#6b7280', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: '#3b82f6', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
              >
                {saving ? 'Saving…' : '✓ Save'}
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

// Small helpers for the drawer
const InfoRow: React.FC<{ icon: string; label: string; value: string }> = ({ icon, label, value }) => (
  <div>
    <div style={{ fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{icon} {label}</div>
    <div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{value}</div>
  </div>
);
const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginBottom: 12 }}>
    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</label>
    {children}
  </div>
);
const inp: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: 7,
  border: '1.5px solid #e5e7eb', fontSize: 13, boxSizing: 'border-box',
  outline: 'none', fontFamily: 'inherit',
};

// ─── Compact booking card (list view) ─────────────────────────────────────────
const BookingCard: React.FC<{ b: BookingRow; onClick: () => void }> = ({ b, onClick }) => {
  const sc = getStatus(b.status);
  const custName = b.customer_name || b.customerName || 'Guest';
  const staffName = b.staff_name || b.staffName || '';
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '11px 14px', borderRadius: 10,
        border: '1px solid #f3f4f6',
        borderLeft: `3px solid ${sc.dot}`,
        background: '#fff', cursor: 'pointer',
        transition: 'box-shadow 0.15s, transform 0.15s',
        marginBottom: 6,
      }}
      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 3px 12px rgba(0,0,0,0.08)'; (e.currentTarget as HTMLElement).style.transform = 'translateX(2px)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = 'none'; (e.currentTarget as HTMLElement).style.transform = 'none'; }}
    >
      {/* Status dot */}
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: sc.dot, flexShrink: 0 }} />

      {/* Main info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {b.service}
        </div>
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 1 }}>
          👤 {custName}
          {staffName ? ` · 🧑‍💼 ${staffName}` : ''}
        </div>
      </div>

      {/* Date + status badge */}
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{fmtDateTime(b)}</div>
        <span style={{
          display: 'inline-block', marginTop: 3,
          padding: '2px 8px', borderRadius: 20,
          fontSize: 11, fontWeight: 700,
          background: sc.bg, color: sc.text,
        }}>
          {b.status}
        </span>
      </div>

      {/* Chevron */}
      <div style={{ color: '#d1d5db', fontSize: 14, flexShrink: 0 }}>›</div>
    </div>
  );
};

// ─── Main page ─────────────────────────────────────────────────────────────────
const BookingPage: React.FC = () => {
  const { token, role } = useAuth();
  const navigate = useNavigate();
  const isStaff = role?.toLowerCase() === 'staff';

  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  React.useEffect(() => {
    const h = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const [customers,  setCustomers]  = useState<Customer[]>([]);
  const [staffList,  setStaffList]  = useState<Staff[]>([]);
  const [bookings,   setBookings]   = useState<BookingRow[]>([]);
  const [services,   setServices]   = useState<{id:number;name:string;price:number}[]>([]);
  const [loading,    setLoading]    = useState(true);

  // Form
  const [customerId,      setCustomerId]      = useState('');
  const [customerSearch,  setCustomerSearch]  = useState('');
  const [showDropdown,    setShowDropdown]    = useState(false);
  const [isGuest,         setIsGuest]         = useState(false);
  const [guestName,       setGuestName]       = useState('');
  const [guestEmail,      setGuestEmail]      = useState('');
  const [service,         setService]         = useState('');
  const [date,            setDate]            = useState('');
  const [time,            setTime]            = useState('');
  const [notes,           setNotes]           = useState('');
  const [staffId,         setStaffId]         = useState('');
  const [locationVal,     setLocationVal]     = useState('');
  const [meetingLink,     setMeetingLink]     = useState('');

  // View / filter
  const [statusFilter,  setStatusFilter]  = useState('all');
  const [view,          setView]          = useState<'list' | 'calendar'>('calendar');

  // Drawer
  const [drawerBooking, setDrawerBooking] = useState<BookingRow | null>(null);

  // ── Data loading ────────────────────────────────────────────────────────────
  const loadBookings = async () => {
    if (!token) return;
    try {
      const res = await apiFetch(`${API_URL}/bookings`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setBookings(Array.isArray(await res.json().then(d => d)) ? await apiFetch(`${API_URL}/bookings`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()) : []);
    } catch { /* silent */ }
  };

  // cleaner version of loadBookings used internally
  const fetchBookings = async () => {
    if (!token) return;
    try {
      const res = await apiFetch(`${API_URL}/bookings`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setBookings(Array.isArray(data) ? data : []);
      }
    } catch { console.warn('Failed to fetch bookings'); }
  };

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    setLoading(true);

    apiFetch(`${API_URL}/customers`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then((data: any) => setCustomers(Array.isArray(data) ? data.map((c: any) => ({ id: c._id || c.id, name: c.name, email: c.email })) : []))
      .catch(() => setCustomers([]));

    apiFetch(`${API_URL}/bookings/staff`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then((data: any) => { const raw = Array.isArray(data) ? data : (data.rows || []); setStaffList(Array.isArray(raw) ? raw : []); })
      .catch(() => setStaffList([]));

    apiFetch(`${API_URL}/products`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then((data: any) => { const raw = Array.isArray(data) ? data : (data.rows || data.products || []); setServices(Array.isArray(raw) ? raw.map((p: any) => ({ id: p.id, name: p.name, price: p.price })) : []); })
      .catch(() => setServices([]));

    fetchBookings().finally(() => setLoading(false));
  }, [token, navigate]);

  // ── Create booking ──────────────────────────────────────────────────────────
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return navigate('/login');

    const hasCustomer = isGuest ? guestName.trim() !== '' : customerId !== '';
    if (!hasCustomer || !service || !date || !time) {
      toast.error('Please fill Customer, Service, Date and Time');
      return;
    }
    // Prevent same-time same-day double-booking for same customer
    const conflict = bookings.some(b => {
      const bDate = b.date || (b.start ? b.start.split('T')[0] : undefined);
      const bTime = b.time || (b.start ? b.start.split('T')[1]?.slice(0,5) : undefined);
      if (!bDate || !bTime) return false;

      const sameCustomer = !isGuest
        ? (b.customer_id === customerId || b.customerId === customerId || b.customer_id === customerId)
        : (b.customer_name === guestName || b.customerName === guestName);

      return sameCustomer && bDate === date && bTime === time;
    });

    if (conflict) {
      toast.error('This customer already has a booking at the same date and time. Please choose another slot.');
      return;
    }
    const startIso = new Date(`${date}T${time}`).toISOString();
    const endIso   = new Date(new Date(startIso).getTime() + 60 * 60000).toISOString();

    const payload: any = {
      customerId:    isGuest ? null : customerId,
      customerName:  isGuest ? guestName  : customers.find(c => c.id === customerId)?.name,
      customerEmail: isGuest ? guestEmail : customers.find(c => c.id === customerId)?.email,
      service, date, time,
      startTime: startIso, endTime: endIso,
      start: startIso, end: endIso,
      duration: 60, notes,
      location: locationVal, meetingLink,
    };
    if (staffId) { payload.staffId = staffId; payload.staff_id = staffId; }

    try {
      const res = await apiFetch(`${API_URL}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success('Booking created!');
        setService(''); setDate(''); setTime(''); setNotes(''); setStaffId('');
        setLocationVal(''); setMeetingLink(''); setCustomerId(''); setCustomerSearch('');
        setGuestName(''); setGuestEmail(''); setIsGuest(false);
        fetchBookings();
      } else {
        const err = await res.json();
        toast.error(res.status === 409 ? (err.error || 'Time slot already booked.') : 'Unable to create booking.');
      }
    } catch { toast.error('Network error — please try again'); }
  };

  // ── Save edit from drawer ───────────────────────────────────────────────────
  const handleSaveEdit = async (id: string, patch: Partial<BookingRow>) => {
    if (!token) return;
    try {
      const res = await apiFetch(`${API_URL}/bookings/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(patch),
      });
      if (res.ok) {
        toast.success('Booking updated');
        setDrawerBooking(null);
        fetchBookings();
      } else {
        const err = await res.json();
        toast.error(err.message || 'Failed to update');
      }
    } catch { toast.error('Network error'); }
  };

  // ── Delete from drawer ──────────────────────────────────────────────────────
  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this booking?')) return;
    try {
      const res = await apiFetch(`${API_URL}/bookings/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok || res.status === 204) {
        toast.success('Booking deleted');
        setDrawerBooking(null);
        fetchBookings();
      } else { toast.error('Failed to delete'); }
    } catch { toast.error('Network error'); }
  };

  // ── Filtered list ────────────────────────────────────────────────────────────
  const filtered = bookings.filter(b => statusFilter === 'all' || b.status === statusFilter);

  const exportBookingsCSV = () => {
    if (!filtered.length) {
      toast.warning('No bookings to export');
      return;
    }
    const escapeCsv = (value: any) => {
      const text = value == null ? '' : String(value);
      if (/[,"\n]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    };
    const headers = ['ID', 'Customer', 'Email', 'Service', 'Date', 'Time', 'Status', 'Staff', 'Location', 'Notes'];
    const rows = filtered.map(b => [
      b.id,
      b.customer_name || b.customerName || 'Guest',
      b.customer_email || b.customerEmail || '',
      b.service || '',
      b.date || (b.start ? new Date(b.start).toLocaleDateString('en-GB') : ''),
      b.time || (b.start ? new Date(b.start).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''),
      b.status || '',
      b.staff_name || b.staffName || '',
      b.location || '',
      b.notes || '',
    ].map(escapeCsv).join(','));
    const csv = `data:text/csv;charset=utf-8,${headers.map(escapeCsv).join(',')}\n${rows.join('\n')}`;
    const link = document.createElement('a');
    link.setAttribute('href', encodeURI(csv));
    link.setAttribute('download', `bookings_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ── Calendar events ──────────────────────────────────────────────────────────
  const staffColorMap = useMemo(() => {
    const m: Record<string, string> = {};
    staffList.forEach((s, i) => { m[s.id] = STAFF_COLORS[i % STAFF_COLORS.length]; });
    return m;
  }, [staffList]);

  const calendarEvents = useMemo(() => filtered.map(b => {
    const rawStart = b.start_time || b.startTime || b.start || (b.date && b.time ? `${b.date}T${b.time}` : null);
    const rawEnd   = b.end_time   || b.endTime   || b.end   || null;
    if (!rawStart) return null;
    const start = new Date(rawStart);
    const end   = rawEnd ? new Date(rawEnd) : new Date(start.getTime() + 60 * 60000);
    if (isNaN(start.getTime())) return null;
    return { id: b.id, title: `${b.service} – ${b.customer_name || b.customerName || 'Guest'}`, start, end, resource: b };
  }).filter(Boolean) as { id: string; title: string; start: Date; end: Date; resource: BookingRow }[], [filtered]);

  const calendarEventStyle = (event: any) => {
    const b = event.resource as BookingRow;
    // Use consistent STATUS palette colours (dot colour = background)
    const sc = getStatus(b.status);
    const opacity = b.status?.toLowerCase() === 'cancelled' ? 0.45 : 0.92;
    return { style: { backgroundColor: sc.dot, borderRadius: 4, opacity, color: '#fff', border: 'none' } };
  };

  // ─── Render ──────────────────────────────────────────────────────────────────
  const isMobile = windowWidth < 900;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '12px' : '20px 16px', paddingBottom: 64 }}>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        .booking-root * { font-family: 'DM Sans', system-ui, sans-serif; box-sizing: border-box; }
        .booking-root input:focus, .booking-root select:focus, .booking-root textarea:focus {
          outline: none; border-color: #3b82f6 !important;
          box-shadow: 0 0 0 3px rgba(59,130,246,0.15);
        }
        .rbc-calendar { font-family: 'DM Sans', system-ui, sans-serif; }
        .rbc-event { border-radius: 4px !important; }
        .rbc-toolbar { display: none !important; }
      `}</style>

      <div className="booking-root">

        {/* ── Top bar ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#111827' }}>📅 Bookings</h1>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/send-message')}
              style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              <MailOutlined />
              Send Message
            </button>
            <button
              onClick={exportBookingsCSV}
              style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              <DownloadOutlined />
              Export CSV
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '320px 1fr', gap: 16, alignItems: 'start' }}>

          {/* ══ LEFT: Create form ══ */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 18, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: '1px solid #f3f4f6' }}>
            <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 800, color: '#111827' }}>New Booking</h3>
            <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

              {/* Customer */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <label style={lbl}>Customer *</label>
                  <button
                    type="button"
                    onClick={() => { setIsGuest(!isGuest); setCustomerId(''); setCustomerSearch(''); setGuestName(''); setGuestEmail(''); }}
                    style={{ fontSize: 11, background: isGuest ? '#3b82f6' : '#f3f4f6', color: isGuest ? '#fff' : '#555', border: 'none', borderRadius: 5, padding: '2px 8px', cursor: 'pointer', fontWeight: 600 }}
                  >
                    {isGuest ? '👤 Guest ON' : '+ Guest'}
                  </button>
                </div>

                {isGuest ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <input placeholder="Guest name *" value={guestName} onChange={e => setGuestName(e.target.value)} style={finp} />
                    <input placeholder="Guest email (optional)" type="email" value={guestEmail} onChange={e => setGuestEmail(e.target.value)} style={finp} />
                  </div>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      placeholder="Search customer…"
                      value={customerSearch}
                      onChange={e => { setCustomerSearch(e.target.value); setCustomerId(''); setShowDropdown(true); }}
                      onFocus={() => setShowDropdown(true)}
                      onBlur={() => setTimeout(() => setShowDropdown(false), 180)}
                      style={{ ...finp, borderColor: customerId ? '#22c55e' : undefined }}
                    />
                    {customerId && <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#22c55e', fontSize: 12, fontWeight: 700 }}>✓</span>}
                    {showDropdown && customerSearch.length > 0 && (
                      <div style={{ position: 'absolute', zIndex: 999, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, width: '100%', maxHeight: 170, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', top: '100%', marginTop: 2 }}>
                        {customers.filter(c => c.name.toLowerCase().includes(customerSearch.toLowerCase())).slice(0, 8).map(c => (
                          <div
                            key={c.id}
                            onMouseDown={() => { setCustomerId(c.id); setCustomerSearch(c.name); setShowDropdown(false); }}
                            style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f9fafb', fontSize: 13 }}
                            onMouseEnter={e => (e.currentTarget.style.background = '#eff6ff')}
                            onMouseLeave={e => (e.currentTarget.style.background = '')}
                          >
                            <strong>{c.name}</strong>
                            {c.email && <span style={{ color: '#9ca3af', marginLeft: 6, fontSize: 12 }}>{c.email}</span>}
                          </div>
                        ))}
                        {customers.filter(c => c.name.toLowerCase().includes(customerSearch.toLowerCase())).length === 0 && (
                          <div style={{ padding: '8px 12px', color: '#9ca3af', fontSize: 13 }}>No customers found</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Service */}
              <div>
                <label style={lbl}>Service *</label>
                {services.length > 0 ? (
                  <select value={service} onChange={e => setService(e.target.value)} style={finp} required>
                    <option value="">Select a service</option>
                    {services.map(s => <option key={s.id} value={s.name}>{s.name}{s.price ? ` — £${s.price}` : ''}</option>)}
                    <option value="__custom__">Other (type below)</option>
                  </select>
                ) : (
                  <input value={service} onChange={e => setService(e.target.value)} style={finp} required placeholder="e.g. Consultation" />
                )}
                {service === '__custom__' && (
                  <input value={''} onChange={e => setService(e.target.value)} style={{ ...finp, marginTop: 6 }} placeholder="Describe the service…" required autoFocus />
                )}
              </div>

              {/* Staff */}
              {!isStaff ? (
                <div>
                  <label style={lbl}>Staff <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
                  <select value={staffId} onChange={e => setStaffId(e.target.value)} style={finp}>
                    <option value="">Auto-assign / No preference</option>
                    {staffList.map(s => <option key={s.id} value={s.id}>{s.name}{s.role ? ` (${s.role})` : ''}</option>)}
                  </select>
                </div>
              ) : (
                <div style={{ padding: '7px 10px', background: '#f0fdf4', borderRadius: 7, fontSize: 12, color: '#166534', fontWeight: 500 }}>
                  🧑‍💼 Assigned to: You (auto)
                </div>
              )}

              {/* Date + Time */}
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <label style={lbl}>Date *</label>
                  <input type="date" value={date} onChange={e => setDate(e.target.value)} style={finp} required />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={lbl}>Time *</label>
                  <select value={time} onChange={e => setTime(e.target.value)} style={finp} required>
                    <option value="">-- Select --</option>
                    {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              {/* Location */}
              <div>
                <label style={lbl}>Location</label>
                <select value={locationVal} onChange={e => setLocationVal(e.target.value)} style={finp}>
                  <option value="">None</option>
                  <option value="office">Office</option>
                  <option value="home">Customer's Home</option>
                  <option value="remote">Remote</option>
                </select>
              </div>

              {/* Meeting link */}
              <div>
                <label style={lbl}>Meeting Link</label>
                <input type="url" value={meetingLink} onChange={e => setMeetingLink(e.target.value)} style={finp} placeholder="https://meet.google.com/…" />
              </div>

              {/* Notes */}
              <div>
                <label style={lbl}>Notes</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} style={{ ...finp, height: 56 }} placeholder="Optional notes..." />
              </div>

              <button
                type="submit"
                style={{ padding: '10px 16px', borderRadius: 8, border: 'none', background: '#3b82f6', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', marginTop: 2 }}
              >
                Create Booking
              </button>
            </form>
          </div>

          {/* ══ RIGHT: List / Calendar ══ */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 18, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: '1px solid #f3f4f6' }}>

            {/* Toolbar */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#111827' }}>
                {view === 'list'
                  ? <>All Bookings <span style={{ color: '#9ca3af', fontWeight: 500, fontSize: 13 }}>({filtered.length})</span></>
                  : 'Calendar'}
              </h3>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                {/* Toggle */}
                <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: 8, padding: 2 }}>
                  {(['list', 'calendar'] as const).map(v => (
                    <button
                      key={v}
                      onClick={() => setView(v)}
                      style={{ border: 'none', padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 12, transition: 'all 0.15s', background: view === v ? '#fff' : 'transparent', color: view === v ? '#3b82f6' : '#6b7280', boxShadow: view === v ? '0 1px 4px rgba(0,0,0,0.10)' : 'none' }}
                    >
                      {v === 'list' ? '☰ List' : '📅 Calendar'}
                    </button>
                  ))}
                </div>
                {/* Status filter */}
                {view === 'list' && (
                  <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ ...finp, width: 'auto', padding: '5px 8px', fontSize: 12 }}>
                    <option value="all">All Status</option>
                    <option value="Pending">Pending</option>
                    <option value="Confirmed">Confirmed</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                )}
                <button onClick={fetchBookings} style={{ padding: '6px 12px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#f9fafb', color: '#374151', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                  ↻ Refresh
                </button>
              </div>
            </div>

            {loading && <p style={{ color: '#9ca3af', textAlign: 'center', padding: 40 }}>Loading…</p>}

            {/* ── LIST VIEW — compact cards, NO table ── */}
            {!loading && view === 'list' && (
              <>
                {filtered.length === 0 && (
                  <p style={{ color: '#9ca3af', textAlign: 'center', padding: 40 }}>No bookings found.</p>
                )}
                {/* Status summary pills */}
                {filtered.length > 0 && (
                  <>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                      {Object.entries(STATUS).map(([s, c]) => {
                        const count = filtered.filter(b => b.status === s).length;
                        if (count === 0) return null;
                        return (
                          <span
                            key={s}
                            onClick={() => setStatusFilter(statusFilter === s ? 'all' : s)}
                            style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: c.bg, color: c.text, cursor: 'pointer', border: `1px solid ${c.dot}22` }}
                          >
                            {s} · {count}
                          </span>
                        );
                      })}
                    </div>
                    <div style={{ maxHeight: isMobile ? 'none' : 520, overflowY: 'auto', paddingRight: 2 }}>
                      {filtered.map(b => (
                        <BookingCard key={b.id} b={b} onClick={() => setDrawerBooking(b)} />
                      ))}
                    </div>
                  </>
                )}
              </>
            )}

            {/* ── CALENDAR VIEW ── */}
            {!loading && view === 'calendar' && (
              <>
                <div style={{ height: isMobile ? 420 : 560, overflowX: 'auto' }}>
                  <Calendar
                    localizer={localizer}
                    events={calendarEvents}
                    startAccessor="start"
                    endAccessor="end"
                    eventPropGetter={calendarEventStyle}
                    views={['month', 'week', 'day', 'agenda']}
                    defaultView="month"
                    popup
                    step={30}
                    showMultiDayTimes
                    onSelectEvent={(event: any) => setDrawerBooking(event.resource as BookingRow)}
                    formats={{ timeGutterFormat: 'HH:mm', agendaTimeFormat: 'HH:mm' }}
                    style={{ fontFamily: 'inherit' }}
                    components={{ toolbar: CalendarToolbar }}
                  />
                </div>
                {/* Colour legend — matches STATUS palette exactly */}
                <div style={{ marginTop: 12, display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, color: '#6b7280' }}>
                  {Object.entries(STATUS).map(([s, c]) => (
                    <span key={s} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: c.dot, display: 'inline-block' }} />
                      {s}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Full detail drawer ── */}
      {drawerBooking && (
        <BookingDrawer
          booking={drawerBooking}
          onClose={() => setDrawerBooking(null)}
          onSave={handleSaveEdit}
          onDelete={handleDelete}
        />
      )}

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick theme="light" />
    </div>
  );
};

// Shared form input style
const lbl: React.CSSProperties = { display: 'block', fontWeight: 600, fontSize: 12, color: '#374151', marginBottom: 3 };
const finp: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: 7, border: '1.5px solid #e5e7eb', fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' };

export default BookingPage;